import { useState, useEffect, useCallback, useRef } from 'react';
import { UseClickableTooltipOptions } from '../types';

export const useClickableTooltip = ({
  triggerRef,
  tooltipRef,
  hideDelay = 0,
  showOnHover = false
}: UseClickableTooltipOptions) => {
  const [isVisible, setIsVisible] = useState(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // Handle click to show tooltip
  const handleTriggerClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      clearHideTimeout();

      // Dispatch a custom event to close all other tooltips
      document.dispatchEvent(new CustomEvent('closeAllTooltips'));

      // Open this tooltip
      setIsVisible(true);
    },
    [clearHideTimeout]
  );

  // Handle hover to show tooltip (if enabled)
  const handleTriggerMouseEnter = useCallback(() => {
    if (showOnHover) {
      clearHideTimeout();
      setIsVisible(true);
    }
  }, [showOnHover, clearHideTimeout]);

  // Handle hover to hide tooltip (if enabled)
  const handleTriggerMouseLeave = useCallback(() => {
    if (showOnHover && hideDelay > 0) {
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, hideDelay);
    } else if (showOnHover) {
      setIsVisible(false);
    }
  }, [showOnHover, hideDelay]);

  // Handle tooltip hover to keep it visible
  const handleTooltipMouseEnter = useCallback(() => {
    if (showOnHover) {
      clearHideTimeout();
    }
  }, [showOnHover, clearHideTimeout]);

  // Handle tooltip leave to hide it
  const handleTooltipMouseLeave = useCallback(() => {
    if (showOnHover && hideDelay > 0) {
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, hideDelay);
    } else if (showOnHover) {
      setIsVisible(false);
    }
  }, [showOnHover, hideDelay]);

  // Handle click outside to hide tooltip
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      // Close tooltip if click is outside the tooltip itself
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setIsVisible(false);
      }
    },
    [tooltipRef]
  );

  // Add click event listener to trigger element
  useEffect(() => {
    const triggerElement = triggerRef.current;
    if (triggerElement) {
      triggerElement.addEventListener('click', handleTriggerClick);

      if (showOnHover) {
        triggerElement.addEventListener('mouseenter', handleTriggerMouseEnter);
        triggerElement.addEventListener('mouseleave', handleTriggerMouseLeave);
      }

      return () => {
        triggerElement.removeEventListener('click', handleTriggerClick);
        if (showOnHover) {
          triggerElement.removeEventListener('mouseenter', handleTriggerMouseEnter);
          triggerElement.removeEventListener('mouseleave', handleTriggerMouseLeave);
        }
      };
    }
  }, [triggerRef, handleTriggerClick, showOnHover, handleTriggerMouseEnter, handleTriggerMouseLeave]);

  // Add click outside listener when tooltip is visible
  useEffect(() => {
    if (isVisible) {
      document.addEventListener('click', handleClickOutside);

      // Listen for custom event to close all tooltips
      const handleCloseAllTooltips = () => {
        setIsVisible(false);
      };
      document.addEventListener('closeAllTooltips', handleCloseAllTooltips);

      return () => {
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('closeAllTooltips', handleCloseAllTooltips);
      };
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isVisible, handleClickOutside]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearHideTimeout();
    };
  }, [clearHideTimeout]);

  const closeTooltip = useCallback(() => {
    clearHideTimeout();
    setIsVisible(false);
  }, [clearHideTimeout]);

  const openTooltip = useCallback(() => {
    clearHideTimeout();
    setIsVisible(true);
  }, [clearHideTimeout]);

  return {
    isVisible,
    closeTooltip,
    openTooltip,
    handleTooltipMouseEnter,
    handleTooltipMouseLeave
  };
};
